import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Loader2,
  Search,
  FileText,
  RefreshCw,
  CheckCircle,
  Copy,
  Building,
  FileSearch,
  BadgeCheck,
  Filter,
  Link,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import type {
  RefinementSectionType,
  RefinementActionId,
} from "@shared/refinement-types";
import { getActionsForSection } from "@shared/refinement-types";

// Map icon names to components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Search,
  FileText,
  RefreshCw,
  CheckCircle,
  Copy,
  Building,
  Sparkles,
  FileSearch,
  BadgeCheck,
  Filter,
  Link,
};

interface RefineButtonProps {
  sectionType: RefinementSectionType;
  sessionId: string;
  companyName?: string;
  onRefineComplete: () => void;
  disabled?: boolean;
}

export function RefineButton({
  sectionType,
  sessionId,
  companyName,
  onRefineComplete,
  disabled = false,
}: RefineButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeAction, setActiveAction] = useState<RefinementActionId | null>(
    null
  );

  const actions = getActionsForSection(sectionType);

  // tRPC mutations
  const zefixLookup = trpc.discoveryRefinement.lookupZefix.useMutation();
  const normalizeVendors =
    trpc.discoveryRefinement.normalizeVendors.useMutation();
  const normalizeCustomers =
    trpc.discoveryRefinement.normalizeCustomers.useMutation();
  const validateBankAccounts =
    trpc.discoveryRefinement.validateBankAccounts.useMutation();
  const parseDescriptions =
    trpc.discoveryRefinement.parseVendorDescriptions.useMutation();
  const extractBankMetadata =
    trpc.discoveryRefinement.extractFromBankMetadata.useMutation();
  const detectDuplicates =
    trpc.discoveryRefinement.detectDuplicates.useMutation();
  const extractBankInfo =
    trpc.discoveryRefinement.extractBankInfo.useMutation();
  const validateVendorsZefix =
    trpc.discoveryRefinement.validateVendorsZefix.useMutation();
  const lookupCustomersZefix =
    trpc.discoveryRefinement.lookupCustomersZefix.useMutation();
  const matchToDb = trpc.discoveryRefinement.matchToDb.useMutation();
  const llmReanalyze = trpc.discoveryRefinement.llmReanalyze.useMutation();

  const handleAction = async (actionId: RefinementActionId) => {
    setIsLoading(true);
    setActiveAction(actionId);

    try {
      switch (actionId) {
        case "zefix-lookup":
          if (!companyName) {
            toast.error("Company name required for Zefix lookup");
            break;
          }
          const zefixResult = await zefixLookup.mutateAsync({
            sessionId,
            companyName,
          });
          if (zefixResult.candidates && zefixResult.candidates.length > 0) {
            toast.success(
              `Found ${zefixResult.candidates.length} Zefix matches`
            );
          } else {
            toast.info("No Zefix matches found");
          }
          break;

        case "normalize-vendors":
          const vendorResult = await normalizeVendors.mutateAsync({
            sessionId,
          });
          toast.success(
            `Normalized vendors: ${vendorResult.itemsAffected} cleaned`
          );
          break;

        case "normalize-customers":
          const customerResult = await normalizeCustomers.mutateAsync({
            sessionId,
          });
          toast.success(
            `Normalized customers: ${customerResult.itemsAffected} cleaned`
          );
          break;

        case "validate-ibans":
          const bankResult = await validateBankAccounts.mutateAsync({
            sessionId,
          });
          toast.success(`Validated ${bankResult.itemsAffected} bank accounts`);
          break;

        case "parse-descriptions":
          const parseResult = await parseDescriptions.mutateAsync({
            sessionId,
          });
          toast.success(
            `Re-parsed ${parseResult.itemsAffected} vendor descriptions`
          );
          break;

        case "extract-bank-metadata":
          const metadataResult = await extractBankMetadata.mutateAsync({
            sessionId,
          });
          if (metadataResult.success) {
            toast.success("Extracted company info from bank statements");
          } else {
            toast.info("No additional info found in bank metadata");
          }
          break;

        case "filter-noise":
          // Filter noise is part of normalizeVendors
          const noiseResult = await normalizeVendors.mutateAsync({ sessionId });
          toast.success(`Filtered ${noiseResult.itemsAffected} noise entries`);
          break;

        case "zefix-validate-vendors":
          const zefixVendorResult = await validateVendorsZefix.mutateAsync({
            sessionId,
          });
          if (zefixVendorResult.success) {
            toast.success(
              `Validated ${zefixVendorResult.itemsAffected} vendors via Zefix`
            );
          } else {
            toast.info(
              zefixVendorResult.message || "No vendors matched in Zefix"
            );
          }
          break;

        case "zefix-lookup-customers":
          const zefixCustomerResult = await lookupCustomersZefix.mutateAsync({
            sessionId,
          });
          if (zefixCustomerResult.success) {
            toast.success(
              `Validated ${zefixCustomerResult.itemsAffected} customers via Zefix`
            );
          } else {
            toast.info(
              zefixCustomerResult.message || "No customers matched in Zefix"
            );
          }
          break;

        case "match-to-db":
          const matchResult = await matchToDb.mutateAsync({ sessionId });
          if (matchResult.success) {
            toast.success(
              `Matched ${matchResult.itemsAffected} customers to existing database records`
            );
          } else {
            toast.info(
              matchResult.message || "No matching customers found in database"
            );
          }
          break;

        case "detect-duplicates":
          const dupeResult = await detectDuplicates.mutateAsync({ sessionId });
          if (dupeResult.success) {
            toast.success(
              `Found and merged ${dupeResult.itemsAffected} duplicate bank accounts`
            );
          } else {
            toast.info(dupeResult.message || "No duplicates detected");
          }
          break;

        case "extract-bank-info":
          const bankInfoResult = await extractBankInfo.mutateAsync({
            sessionId,
          });
          if (bankInfoResult.success) {
            toast.success(
              `Extracted bank info for ${bankInfoResult.itemsAffected} accounts`
            );
          } else {
            toast.info(
              bankInfoResult.message || "No bank info could be extracted"
            );
          }
          break;

        case "llm-reanalyze":
          const llmResult = await llmReanalyze.mutateAsync({ sessionId });
          if (llmResult.success) {
            toast.success(
              "Re-analyzed documents with AI - company info updated"
            );
          } else {
            toast.info(
              llmResult.message || "Could not extract additional information"
            );
          }
          break;

        default:
          toast.error(`Unknown action: ${actionId}`);
      }

      onRefineComplete();
    } catch (error) {
      console.error("Refinement error:", error);
      toast.error(error instanceof Error ? error.message : "Refinement failed");
    } finally {
      setIsLoading(false);
      setActiveAction(null);
    }
  };

  if (actions.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled || isLoading}
          className="h-7 px-2 text-muted-foreground hover:text-foreground"
          onClick={e => e.stopPropagation()}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          <span className="ml-1 text-xs">Refine</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          Refinement Actions
        </div>
        <DropdownMenuSeparator />
        {actions.map(action => {
          const IconComponent = iconMap[action.icon] || Sparkles;
          const isActive = activeAction === action.id;

          return (
            <DropdownMenuItem
              key={action.id}
              onClick={e => {
                e.stopPropagation();
                handleAction(action.id);
              }}
              disabled={isLoading}
              className="flex flex-col items-start gap-0.5 py-2"
            >
              <div className="flex items-center gap-2 w-full">
                {isActive ? (
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                ) : (
                  <IconComponent className="h-4 w-4 shrink-0" />
                )}
                <span className="font-medium">{action.label}</span>
              </div>
              <span className="text-xs text-muted-foreground pl-6">
                {action.description}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
